'use client';

import { usePinakesCard } from '@/hooks/usePinakesCard/usePinakesCard';
import { fileNameFromUrl, inferMediaKindFromUrl } from '@/libs/file/inlineMediaKind';
import { ResourceCard } from '@/molecules/ResourceCard/ResourceCard';
import { getResourceStatus } from '@/molecules/ResourceCard/ResourceCard.utils';
import { ResourceCardSkeleton } from '@/molecules/ResourceCardSkeleton/ResourceCardSkeleton';
import { PinakesStatus, type TPinakesResourceDetail } from '@/services/pinakes/pinakes.types';
import { GenericPreview } from './GenericPreview';

const IMAGE_FILE_EXTENSION = /\.(avif|bmp|gif|jpe?g|png|svg|webp)$/i;

/**
 * A link straight to an image, video or audio file: `GenericPreview` shows or plays it inline, which
 * a card would replace, so such a link is not looked up in Pinakes. The media helpers read `https:`
 * links only, so an `http:` link (also what a link written without a scheme becomes) is read as its
 * `https:` form: only its file extension matters here.
 */
function isDirectMediaFile(url: string): boolean {
  const fileUrl = url.replace(/^http:/i, 'https:');
  const kind = inferMediaKindFromUrl(fileUrl);
  if (kind === 'video' || kind === 'audio') return true;
  const fileName = fileNameFromUrl(fileUrl);
  return fileName !== null && IMAGE_FILE_EXTENSION.test(fileName);
}

/**
 * A card with neither details nor a name says less than the link's own preview: a `pending` or
 * `error` card without a name keeps `GenericPreview`. A `missing` or `unreachable` card is still
 * shown, since it tells the viewer something about the link.
 */
function hasNothingToShow(card: TPinakesResourceDetail): boolean {
  const status = getResourceStatus(card.status);
  return !card.name && (status === PinakesStatus.PENDING || status === PinakesStatus.ERROR);
}

interface GenericResourcePreviewProps {
  url: string;
}

/**
 * A generic link's preview: its external resource card when Pinakes has one, else the plain link
 * preview. Without a Pinakes origin configured no request is made and `GenericPreview` renders at
 * once; an unknown link (404), a failed read, a direct media file and a card with nothing to show
 * fall back to it too.
 */
export function GenericResourcePreview({ url }: GenericResourcePreviewProps) {
  const { data: card, isLoading } = usePinakesCard(isDirectMediaFile(url) ? null : url);

  if (isLoading) return <ResourceCardSkeleton />;
  if (!card || hasNothingToShow(card)) return <GenericPreview url={url} />;
  return <ResourceCard card={card} href={url} />;
}
